// @flow
import { App } from './jsti/core';
//
import './bootstrap';
//
import DisableDraggingPlugin from './plugins/DisableDraggingPlugin';
import Store from './classes/Store';
import LivewireErrorHandler from './classes/LivewireErrorHandler';
import type { InsiderCampaign, InsiderUnprocessedProduct } from './types/UseInsider';
import { flare } from '@flareapp/flare-client';
import Scroll from './components/alpine/Scroll';
import SearchHighlights from './components/alpine/SearchHighlights';
import ImageSlider from './components/alpine/ImageSlider';

const INSIDER_POLL_INTERVAL_MS = 50;
const INSIDER_MAX_POLL_ATTEMPTS = 200;

class MedPetsApp extends App {
  /**
   *
   */
  static initIE(): void {
    const ua: string = navigator.userAgent.toLowerCase();
    const isIE: boolean = (ua.indexOf('msie') > -1) || ua.indexOf('trident/') > -1;
    const isEdge: boolean = ua.indexOf('edge') > -1;
    const isFirefox: boolean = ua.includes('firefox');
    const html: HTMLHtmlElement | null = document.querySelector('html');
    if (html) {
      if (isIE) {
        html.classList.add('ie11');
      }
      if (isEdge) {
        html.classList.add('ms-edge');
      }
      if (isFirefox) {
        html.classList.add('firefox');
      }
    }
  }

  /**
   *
   */
  static initTouch(): void {
    const ua: string = navigator.userAgent.toLowerCase();
    const isiPhone: boolean = ua.indexOf('iphone') !== -1;
    const isiPad: boolean = ua.indexOf('ipad') !== -1;
    const isiPod: boolean = ua.indexOf('ipod') !== -1;
    const isIOS: boolean = (isiPhone || isiPad || isiPod);
    const isAndroid: boolean = (ua.indexOf('android') > -1);
    const isTouch: boolean = !!('ontouchstart' in window);
    const html: HTMLHtmlElement | null = document.querySelector('html');
    if (html) {
      if (isIOS) {
        html.classList.add('ios');
      }
      if (isiPhone) {
        html.classList.add('iphone');
      }
      if (isiPad) {
        html.classList.add('ipad');
      }
      if (isAndroid) {
        html.classList.add('android');
      }
      if (isTouch) {
        html.classList.add('touch');
      }
    }
  }

  /**
   * Initialize the app
   */
  init(): void {
    window.store = new Store();

    super.init();
    MedPetsApp.initIE();
    MedPetsApp.initTouch();
    new DisableDraggingPlugin().activate();
    LivewireErrorHandler.register();

    setTimeout(() => {
      window.notification = require('./notifications/Notification').default;
    }, 0);

    this.initializeFlare();
    this.initializeInsider();
    if (!window.store.environment.isAcceptance() && !window.store.environment.isProduction()) {
      const productsMock = [
        {
          simpleSku: 'MP37509',
          visibleSku: 'OP15659',
          url: 'https://www.medpets.nl/frontpro-kauwtabletten-hond?sku=MP37509&#ins_sr=eyJwcm9kdWN0SWQiOiJNUDM3NTA5In0='
        },
        {
          simpleSku: 'MP15541',
          visibleSku: 'OP6070',
          url: 'https://www.medpets.nl/biofood-schapenvet?sku=MP15541&#ins_sr=eyJwcm9kdWN0SWQiOiJNUDE1NTQxIn0='
        },
        {
          simpleSku: 'MP23295',
          visibleSku: 'OP9365',
          url: 'https://www.medpets.nl/hunter-hondentrui-malmo?sku=MP23295&#ins_sr=eyJwcm9kdWN0SWQiOiJNUDIzMjk1In0='
        }
      ];
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('insiderCampaign', {
          detail: {
            campaignId: 2660,
            variationId: 5510,
            products: productsMock
          }
        }
        ));

        window.dispatchEvent(new CustomEvent('insiderCampaign', {
          detail: {
            campaignId: 1418,
            variationId: 5001,
            products: productsMock
          }
        }
        ));

        window.dispatchEvent(new CustomEvent('insiderCampaign', {
          detail: {
            campaignId: 1428,
            variationId: 5040,
            products: productsMock
          }
        }
        ));

        window.dispatchEvent(new CustomEvent('insiderCampaign', {
          detail: {
            campaignId: 1425,
            variationId: 5007,
            products: productsMock
          }
        }
        ));

        window.dispatchEvent(new CustomEvent('insiderCampaign', {
          detail: {
            campaignId: 1431,
            variationId: 2927,
            products: productsMock
          }
        }
        ));

        window.dispatchEvent(new CustomEvent('insiderCampaign', {
          detail: {
            campaignId: 2681,
            variationId: 5554,
            products: productsMock
          }
        }
        ));

        window.dispatchEvent(new CustomEvent('insiderCampaign', {
          detail: {
            campaignId: 2716,
            variationId: 5554,
            products: productsMock
          }
        }
        ));

        window.dispatchEvent(
          new CustomEvent('insiderCampaign', {
            detail: {
              campaignId: 2966,
              variationId: 5554,
              products: productsMock
            }
          })
        );
      }, 2000);
    }

    document.addEventListener('alpine:init', () => {
      window.Alpine.data('scroll', Scroll);
      window.Alpine.data('searchHighlights', SearchHighlights);
      window.Alpine.data('imageSlider', ImageSlider);
    });
  }

  /**
   * Initialize UseInsider
   */
  initializeInsider(): void {
    this.whenInsiderIsReady(function () {
      window.Insider.eventManager.on('ins-sr:only-api-campaign:load', function (event: CustomEvent, data: InsiderCampaign) {
        if (!data || !data.campaignId || !data.products) {
          return;
        }

        window.dispatchEvent(new CustomEvent('insiderCampaign', {
          detail: {
            campaignId: data.campaignId,
            variationId: data.variationId,
            products: data.products.map((product: InsiderUnprocessedProduct) => ({
              simpleSku: product.item_id,
              visibleSku: product.groupcode,
              url: product.url
            }))
          }
        }));
      });
    });
  }

  /**
   * The Insider script loads after our own bundle, so wait for it before subscribing
   */
  whenInsiderIsReady(callback: function): void {
    if (window.Insider?.eventManager) {
      callback();

      return;
    }

    let attempts = 0;

    const interval = setInterval(() => {
      if (window.Insider?.eventManager) {
        clearInterval(interval);
        callback();

        return;
      }

      attempts += 1;

      if (attempts >= INSIDER_MAX_POLL_ATTEMPTS) {
        clearInterval(interval);
      }
    }, INSIDER_POLL_INTERVAL_MS);
  }

  initializeFlare(): void {
    if (!window.store.environment.isLocal()) {
      flare.light();
    }
  }
}

const app = new MedPetsApp();
app.init();

export default MedPetsApp;
